"use client";

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import type { CategoryPublic } from "@/repositories/category.repository";
import { fetchActiveCategoriesRpc } from "@/lib/home/home-public-rpc";
import { fetchCategoryListingCountsRpc } from "@/lib/categories/category-counts-rpc";

export interface CategoryOption {
  id: string;
  title: string;
  name: string;
  icon: string | null;
}

/** Active category for home / browse (same as CategoryPublic). */
export type ActiveCategory = CategoryPublic;

const categoryRefetchDefaults = {
  staleTime: 0,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnMount: true,
  refetchOnReconnect: true,
} as const;

function mapToCategoryOptions(data: CategoryPublic[]): CategoryOption[] {
  return data.map(({ id, title, name, icon }) => ({ id, title, name, icon }));
}

/**
 * Active categories for forms (e.g. create listing) — `supabase.rpc(get_active_categories_public)`.
 * Shares cache with `useActiveCategories` (same query key + queryFn; `select` maps to form options).
 */
export function useCategories(): UseQueryResult<CategoryOption[], Error> {
  return useQuery({
    queryKey: queryKeys.categories.active,
    queryFn: fetchActiveCategoriesRpc,
    select: mapToCategoryOptions,
    ...categoryRefetchDefaults,
  });
}

/** supabase.rpc(get_active_categories_public) — home + dashboard category grid */
export function useActiveCategories(): UseQueryResult<CategoryPublic[], Error> {
  return useQuery({
    queryKey: queryKeys.categories.active,
    queryFn: fetchActiveCategoriesRpc,
    placeholderData: (previousData: CategoryPublic[] | undefined) => previousData,
    ...categoryRefetchDefaults,
  });
}

/** supabase.rpc(get_category_listing_counts) — listing counts per category_id (dashboard browse) */
export function useCategoryCounts(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.categories.counts,
    queryFn: fetchCategoryListingCountsRpc,
    enabled: !!userId,
    ...categoryRefetchDefaults,
  });
}
